/**
 * Layout preferences that are not the theme.
 *
 * The pro ladder, click to trade on the standard book, and the market order's
 * three one-tap amounts — and they live here rather than in `theme.ts` because that module is about two
 * attributes on `<html>` and this is about what the market page's panels do. The two share `uiStore` and
 * `app/stored.ts` and nothing else.
 *
 * The pattern is `theme.ts`'s exactly: state on `uiStore`, storage through
 * `app/stored.ts`, `notify()` called directly because a click is not a frame of
 * market data and there is nothing to coalesce it with.
 */

import {
  uiStore,
  CLICK_TO_TRADE_STORAGE_KEY,
  ONE_TAP_AMOUNTS_STORAGE_KEY,
  ONE_TAP_DEFAULT_AMOUNTS,
  PRO_MODE_STORAGE_KEY,
} from '@/stores/ui'
import { isOneTapAmounts } from '@/domain/one-tap'
import { readStored, readStoredFlag, writeStored, writeStoredFlag } from './stored'

/**
 * Turn the pro ladder on or off.
 *
 * Takes effect on the next render of `<TradeRoute>` and needs no reload: the
 * ladder and the ticket read the same stores, and neither holds anything the
 * other has to hand over.
 */
export function setProMode(on: boolean): void {
  uiStore.mutate((s) => {
    s.proMode = on
  })
  writeStoredFlag(PRO_MODE_STORAGE_KEY, on)
  uiStore.notify()
}

/**
 * Turn click to trade on the standard orderbook on or off.
 *
 * `setProMode`'s shape exactly. The book reads it and so does the ticket that
 * renders the box, and both re-render on the gesture alone.
 */
export function setClickToTrade(on: boolean): void {
  uiStore.mutate((s) => {
    s.clickToTrade = on
  })
  writeStoredFlag(CLICK_TO_TRADE_STORAGE_KEY, on)
  uiStore.notify()
}

/**
 * Save the three buy presets, in their stored spelling (`formatOneTapAmount`).
 *
 * Called once when an edit is saved, never per keystroke: the inputs hold
 * their own text until then.
 */
export function setOneTapAmounts(amounts: readonly [string, string, string]): void {
  const joined = amounts.join(',')
  if (!isOneTapAmounts(joined)) return
  uiStore.mutate((s) => {
    s.oneTapAmounts = [amounts[0], amounts[1], amounts[2]]
  })
  writeStored(ONE_TAP_AMOUNTS_STORAGE_KEY, joined)
  uiStore.notify()
}

/**
 * Read the stored preferences into `uiStore`.
 *
 * Called from `main.tsx` beside `initTheme()`. Order between the two does not
 * matter — both `mutate` rather than `set`, so neither clobbers the other's
 * fields — and that is deliberate rather than lucky: `initTheme` used to
 * replace the whole object, which made the two calls order-dependent in a way
 * nothing would have failed on until a second preference existed.
 *
 * Unlike `initTheme` this installs no listener, so there is nothing to tear
 * down; it returns `void` rather than a no-op teardown to say so.
 */
export function initPreferences(): void {
  const proMode = readStoredFlag(PRO_MODE_STORAGE_KEY, false)
  const clickToTrade = readStoredFlag(CLICK_TO_TRADE_STORAGE_KEY, false)
  const amounts = readStored(ONE_TAP_AMOUNTS_STORAGE_KEY, isOneTapAmounts, ONE_TAP_DEFAULT_AMOUNTS.join(',')).split(',')
  uiStore.mutate((s) => {
    s.proMode = proMode
    s.clickToTrade = clickToTrade
    s.oneTapAmounts = [amounts[0] ?? '5', amounts[1] ?? '25', amounts[2] ?? '100']
  })
  uiStore.notify()
}
